import { Container, Sprite, type Texture } from "pixi.js";
import { SpriteText, SpriteTextFitMode } from "@slot/presentation";
import type { PresentationTaskRunner, SpriteFontDefinition, ViewportStore } from "@slot/presentation";
import { ModalChrome, bindTap } from "@slot/game-shell";
import type { BigWinPort, LocalizationService } from "@slot/game-shell";

/**
 * `BigWinPanel` from the scene dump, y-up flipped (a Cocos angle θ, counter-clockwise, is −θ here): the 1721×1443
 * `ResponsiveContainer` (max width 1721) with the red glow and the `grave` number, the three titles (each a word over
 * the shared "win", which the art draws upright and the scene turns a quarter), the two chainsaws and six blood
 * splatters (their authored size and angle; a burst sets their scale).
 */
const GEOMETRY = {
  content: { width: 1721, height: 1442.8 },
  limits: { desktop: { maxWidth: 1721 }, mobile: { maxWidth: 1721 }, mobile_landscape: {} },
  glow: { y: 179.2, width: 873.2, height: 327, tint: 0xf83838, alpha: 77 / 255 },
  amount: { y: 179.2, maxCharacters: 24, glyphScale: 2, box: { width: 1553.1, height: 293.6 } },
  titles: {
    y: -320.5,
    big: { frame: "big", y: 0, width: 814.2, height: 417.1, winY: 208.3 },
    huge: { frame: "huge", y: -18.2, width: 1031.6, height: 426.1, winY: 212.8 },
    mega: { frame: "mega", y: 0, width: 1031.6, height: 482.6, winY: 241.1 },
    win: { frame: "win", width: 279.8, height: 576, angle: 90 },
  },
  chainsaws: [
    { x: -656.5, y: -629, width: 316.8, height: 514, scaleX: 1, scaleY: 1, angle: 90 },
    { x: 656.5, y: -629, width: 316.8, height: 514, scaleX: 1, scaleY: -1, angle: 90 },
  ],
  splatters: [
    { x: 274.1, y: -31.8, width: 1023.5, height: 1660.4, angle: 0 },
    { x: -156.9, y: 376.2, width: 576.3, height: 873.5, angle: 0 },
    { x: 230.4, y: 488.5, width: 802.6, height: 1302, angle: 59.252 },
    { x: -484.6, y: -671.2, width: 508.3, height: 824.6, angle: 59.252 },
    { x: -357.5, y: -111.2, width: 936.2, height: 1518.8, angle: 27.907 },
    { x: 267.3, y: -848.5, width: 724.3, height: 1175, angle: 27.907 },
  ],
} as const;

/** The server's tier ids that carry a title; "nice" has none (the dimmed number alone). Epic shows the MEGA art. */
const TITLE_BY_TIER: Readonly<Record<string, "big" | "huge" | "mega">> = { big: "big", huge: "huge", epic: "mega" };

/** The big win's motion (the Cocos `BigWinPanelAnimator` constants; seconds of the chrome runner, angles in degrees). */
export const BIG_WIN_MOTION = {
  number: { fromScale: 0.4, fadeInSeconds: 0.2, pulseScale: 1.05, pulseHalfSeconds: 0.35, exitScale: 0.5 },
  settle: { scale: 1.22, upSeconds: 0.18, downSeconds: 0.24 },
  title: { enterOffsetY: -400, enterSeconds: 0.75, pulseScale: 1.06, pulseHalfSeconds: 0.7, bumpScale: 1.28, bumpUpSeconds: 0.16, bumpDownSeconds: 0.22 },
  exitSeconds: 0.28,
  continue: { fadeInSeconds: 0.4, fadeOutSeconds: 0.2 },
  splatter: { fromScale: 1.18, toScale: 0.9, scaleSeconds: 0.05, lifeSeconds: 3, restSeconds: 2, minGapSeconds: 0.6, maxGapSeconds: 1.4, onTitleChange: 2 },
  chainsaw: { fromScale: 0.3, riseY: 80, swingDownDegrees: -35, swingUpDegrees: 18, enterSeconds: 0.42, swingSeconds: 0.3, settleSeconds: 0.26, staggerSeconds: 0.12, idleDegrees: 6, idleBobY: 30, idleHalfSeconds: 0.9 },
} as const;

/** The chrome runner's scopes for the view's loops (nothing slams them: the next motion or a hide ends them). */
const SCOPE = { amountPulse: "big_win_amount_pulse", titlePulse: "big_win_title_pulse", chainsaw: "big_win_chainsaw", splatter: "big_win_splatter" } as const;

/** sine in-out over one half-cycle. */
const sineInOut = (k: number): number => 0.5 - 0.5 * Math.cos(Math.PI * k);

/**
 * A swing that leaves rest for +1 over its first half-cycle, then goes to −1, +1, … (each half sine in-out): the Cocos
 * idle loop of a tween to one side then the other, repeated, started from rest.
 */
function alternate(elapsedSeconds: number, halfSeconds: number): number {
  const half = Math.floor(elapsedSeconds / halfSeconds);
  const k = sineInOut(elapsedSeconds / halfSeconds - half);
  const [a, b] = half === 0 ? [0, 1] : half % 2 === 1 ? [1, -1] : [-1, 1];
  return a + (b - a) * k;
}

/**
 * Teddy's big win behind `BigWinPort` (no Spine): the `grave` number over a red glow, and the tier titles above it.
 * The number pops in alone and breathes while it counts; the first titled tier drops its title in from above and
 * brings the chainsaws (a scale-pop with a down → up swing, then a rock and a bob); each tier upgrade swaps the title and
 * bumps it; blood splatters burst at random through the count-up and on every title change; the count's end bumps the
 * number. Every motion is generation-guarded per channel, so a new one stops the old; a hide stops all.
 */
export class BigWinView extends Container implements BigWinPort {
  public readonly amountOmitsCurrencySymbol = true;
  private readonly chrome: ModalChrome;
  private readonly runner: PresentationTaskRunner;
  private readonly amount: SpriteText | null;
  private readonly titleRoot: Container;
  private readonly titles: ReadonlyMap<"big" | "huge" | "mega", Container>;
  private readonly chainsaws: Array<{ node: Container; base: { x: number; y: number; scaleX: number; scaleY: number; angle: number } }>;
  private readonly splatters: Array<{ node: Container; readyAtSeconds: number }>;
  private readonly generation = { amount: 0, title: 0, chainsaws: 0, splatterLoop: 0, fade: 0 };
  /** Bumped by every hide: a splatter fading from an earlier showing leaves no rest behind. */
  private showing = 0;
  private chainsawsRevealed = false;

  public constructor(input: {
    font: SpriteFontDefinition | null;
    titleSheet: { textures: Record<string, Texture> } | null;
    splatter: Texture | null;
    glow: Texture | null;
    viewport: ViewportStore;
    localization: LocalizationService;
    runner: PresentationTaskRunner;
  }) {
    super({ label: "BigWinPanel" });
    const g = GEOMETRY;
    this.runner = input.runner;
    this.chrome = new ModalChrome({
      label: "root",
      viewport: input.viewport,
      localization: input.localization,
      runner: input.runner,
      content: { ...g.content, limits: g.limits },
    });
    this.addChild(this.chrome);
    const content = new Container({ label: "AnimatedContainer" });
    this.chrome.content.addChild(content);

    const splatterLayer = new Container({ label: "bloodSplatters" });
    this.splatters = input.splatter
      ? g.splatters.map((s, i) => {
          const node = new Container({ label: i === 0 ? "splash" : `splash-00${i}` });
          node.position.set(s.x, s.y);
          node.angle = s.angle;
          const sprite = new Sprite({ texture: input.splatter!, anchor: 0.5 });
          sprite.setSize(s.width, s.height);
          node.addChild(sprite);
          node.visible = false;
          splatterLayer.addChild(node);
          return { node, readyAtSeconds: 0 };
        })
      : [];

    const chainsawLayer = new Container({ label: "chainsaws" });
    const chainsawTexture = input.titleSheet?.textures["chainsaw"] ?? null;
    this.chainsaws = chainsawTexture
      ? g.chainsaws.map((c, i) => {
          const node = new Container({ label: i === 0 ? "chainsaw_left" : "chainsaw_right" });
          const sprite = new Sprite({ texture: chainsawTexture, anchor: 0.5 });
          sprite.setSize(c.width, c.height);
          node.addChild(sprite);
          node.visible = false;
          chainsawLayer.addChild(node);
          return { node, base: { x: c.x, y: c.y, scaleX: c.scaleX, scaleY: c.scaleY, angle: c.angle } };
        })
      : [];

    this.titleRoot = new Container({ label: "TitleSprites" });
    this.titleRoot.position.set(0, g.titles.y);
    const titles = new Map<"big" | "huge" | "mega", Container>();
    const winTexture = input.titleSheet?.textures[g.titles.win.frame] ?? null;
    for (const id of ["big", "huge", "mega"] as const) {
      const spec = g.titles[id];
      const texture = input.titleSheet?.textures[spec.frame];
      if (!texture) continue;
      const title = new Container({ label: `${id}WinTitleSprite` });
      title.position.set(0, spec.y);
      const word = new Sprite({ texture, anchor: 0.5 });
      word.setSize(spec.width, spec.height);
      title.addChild(word);
      if (winTexture) {
        const win = new Sprite({ texture: winTexture, anchor: 0.5, label: "winSprite" });
        win.setSize(g.titles.win.width, g.titles.win.height);
        win.position.set(0, spec.winY);
        win.angle = g.titles.win.angle;
        title.addChild(win);
      }
      title.visible = false;
      this.titleRoot.addChild(title);
      titles.set(id, title);
    }
    this.titles = titles;

    const glow = input.glow ? new Sprite({ texture: input.glow, anchor: 0.5, tint: g.glow.tint, alpha: g.glow.alpha, label: "glow" }) : null;
    if (glow) {
      glow.setSize(g.glow.width, g.glow.height);
      glow.position.set(0, g.glow.y);
    }
    this.amount = input.font
      ? new SpriteText({
          font: input.font,
          maxCharacters: g.amount.maxCharacters,
          glyphScale: g.amount.glyphScale,
          fitMode: SpriteTextFitMode.Width,
          boxWidth: g.amount.box.width,
          boxHeight: g.amount.box.height,
          label: "SpriteText",
        })
      : null;
    this.amount?.position.set(0, g.amount.y);

    content.addChild(splatterLayer, chainsawLayer, this.titleRoot);
    if (glow) content.addChild(glow);
    if (this.amount) content.addChild(this.amount);
    this.hideImmediate();
  }

  public setAmount(formatted: string): void {
    this.amount?.setText(formatted);
  }

  public setSkipVisible(visible: boolean): void {
    this.chrome.skipButton.visible = visible;
  }

  public hideTitles(): void {
    this.generation.title += 1;
    for (const title of this.titles.values()) title.visible = false;
    this.titleRoot.alpha = 0;
  }

  public revealTier(tierId: string, animateEnter: boolean): boolean {
    const id = TITLE_BY_TIER[tierId.toLowerCase()];
    const title = id ? this.titles.get(id) : undefined;
    if (!title) return false;
    for (const other of this.titles.values()) other.visible = other === title;
    this.ensureChainsawsRevealed();
    if (animateEnter) void this.revealTitle();
    else void this.bumpTitle();
    return true;
  }

  /** The panel with its dim at once (the Cocos `root` and its `dim` activate together). */
  public showImmediate(): void {
    this.chrome.stopFade(this.chrome.dim);
    this.chrome.dim.alpha = 1;
    this.chrome.visible = true;
  }

  public hideImmediate(): void {
    for (const channel of ["amount", "title", "fade"] as const) this.generation[channel] += 1;
    this.showing += 1;
    this.resetDecorations();
    this.chrome.stopFade(this.chrome.dim);
    this.chrome.stopFade(this.chrome.continueTextRoot);
    this.chrome.visible = false;
  }

  /** The number alone at its small scale, fading in; the splatter bursts start. */
  public async playEnterAnimation(): Promise<void> {
    const m = BIG_WIN_MOTION;
    this.resetDecorations();
    this.hideTitles();
    this.chrome.continueTextRoot.alpha = 0;
    this.startSplatterBursts();
    const amount = this.amount;
    if (!amount) return;
    amount.scale.set(m.number.fromScale, m.number.fromScale);
    amount.alpha = 0;
    const fade = ++this.generation.fade;
    await this.runner.tweenProgress(
      m.number.fadeInSeconds,
      (k) => {
        if (fade === this.generation.fade && !amount.destroyed) amount.alpha = k;
      },
      undefined,
      { easing: "linear" },
    );
  }

  /**
   * The title rising out and the number shrinking, both fading, with the hint; the chainsaws and the splatters go at
   * once and the loops stop (the Cocos exit). The dim stays until the hide.
   */
  public async playExitAnimation(): Promise<void> {
    const m = BIG_WIN_MOTION;
    for (const channel of ["amount", "title"] as const) this.generation[channel] += 1;
    this.resetDecorations();
    const fade = ++this.generation.fade;
    const amount = this.amount;
    const title = this.titleRoot;
    const from = { amountScale: amount?.scale.x ?? 1, amountAlpha: amount?.alpha ?? 0, titleY: title.position.y, titleAlpha: title.alpha };
    const titleTo = GEOMETRY.titles.y + m.title.enterOffsetY;
    await Promise.all([
      this.chrome.fade(this.chrome.continueTextRoot, 0, m.continue.fadeOutSeconds),
      this.runner.tweenProgress(
        m.exitSeconds,
        (k) => {
          if (fade !== this.generation.fade) return;
          if (!title.destroyed) {
            title.position.y = from.titleY + (titleTo - from.titleY) * k;
            title.alpha = from.titleAlpha * (1 - k);
          }
          if (amount && !amount.destroyed) {
            const scale = from.amountScale + (m.number.exitScale - from.amountScale) * k;
            amount.scale.set(scale, scale);
            amount.alpha = from.amountAlpha * (1 - k);
          }
        },
        undefined,
        { easing: "quadIn" },
      ),
    ]);
  }

  /** The number's breathing through the count-up (from wherever it stands: its first half pops it in). */
  public startAmountPulse(): void {
    const amount = this.amount;
    if (!amount) return;
    const m = BIG_WIN_MOTION.number;
    this.pulse(amount, "amount", SCOPE.amountPulse, m.pulseScale, m.pulseHalfSeconds);
  }

  /** The count lands: the splatters stop, the number bumps, then breathes again. */
  public async playAmountSettleBump(): Promise<void> {
    this.generation.splatterLoop += 1;
    const amount = this.amount;
    if (!amount) return;
    const m = BIG_WIN_MOTION.settle;
    const generation = ++this.generation.amount;
    amount.scale.set(1, 1);
    await this.scaleTween(amount, 1, m.scale, m.upSeconds, "backOut", () => generation === this.generation.amount);
    if (generation !== this.generation.amount) return;
    await this.scaleTween(amount, m.scale, 1, m.downSeconds, "quadOut", () => generation === this.generation.amount);
    if (generation === this.generation.amount) this.startAmountPulse();
  }

  public async fadeInContinueText(): Promise<void> {
    this.chrome.stopFade(this.chrome.continueTextRoot);
    this.chrome.continueTextRoot.alpha = 0;
    await this.chrome.fade(this.chrome.continueTextRoot, 1, BIG_WIN_MOTION.continue.fadeInSeconds);
  }

  public bindSkipTap(onPress: () => void): () => void {
    return bindTap(this.chrome.skipButton, onPress);
  }

  public dispose(): void {
    this.hideImmediate();
    this.chrome.dispose();
    this.destroy({ children: true });
  }

  /** The chainsaws and the splatters gone at once, their loops stopped (an open, an exit, a hide). */
  private resetDecorations(): void {
    this.generation.chainsaws += 1;
    this.generation.splatterLoop += 1;
    this.chainsawsRevealed = false;
    for (const { node } of this.chainsaws) node.visible = false;
    for (const splatter of this.splatters) {
      splatter.node.visible = false;
      splatter.readyAtSeconds = 0;
    }
  }

  /** The first titled tier: the titles drop in from above and fade in, splatters burst, then the title breathes. */
  private async revealTitle(): Promise<void> {
    const m = BIG_WIN_MOTION.title;
    const root = this.titleRoot;
    const generation = ++this.generation.title;
    root.scale.set(1, 1);
    const baseY = GEOMETRY.titles.y;
    root.position.y = baseY + m.enterOffsetY;
    root.alpha = 0;
    this.burstSplatters(BIG_WIN_MOTION.splatter.onTitleChange);
    await Promise.all([
      this.runner.tweenProgress(
        m.enterSeconds,
        (k) => {
          if (generation === this.generation.title && !root.destroyed) root.position.y = baseY + m.enterOffsetY * (1 - k);
        },
        undefined,
        { easing: "backOut" },
      ),
      this.runner.tweenProgress(
        m.enterSeconds,
        (k) => {
          if (generation === this.generation.title && !root.destroyed) root.alpha = k;
        },
        undefined,
        { easing: "linear" },
      ),
    ]);
    if (generation === this.generation.title) this.pulse(root, "title", SCOPE.titlePulse, m.pulseScale, m.pulseHalfSeconds);
  }

  /** A tier upgrade: the swapped title snaps into place, splatters burst, the title bumps, then breathes. */
  private async bumpTitle(): Promise<void> {
    const m = BIG_WIN_MOTION.title;
    const root = this.titleRoot;
    const generation = ++this.generation.title;
    root.scale.set(1, 1);
    root.position.y = GEOMETRY.titles.y;
    root.alpha = 1;
    this.burstSplatters(BIG_WIN_MOTION.splatter.onTitleChange);
    const current = (): boolean => generation === this.generation.title;
    await this.scaleTween(root, 1, m.bumpScale, m.bumpUpSeconds, "backOut", current);
    if (!current()) return;
    await this.scaleTween(root, m.bumpScale, 1, m.bumpDownSeconds, "quadOut", current);
    if (current()) this.pulse(root, "title", SCOPE.titlePulse, m.pulseScale, m.pulseHalfSeconds);
  }

  /**
   * A breathing scale between 1 and `peak` (sine in-out halves) on its channel; its first half runs from wherever the
   * node stands, so the number's first breath is its pop-in.
   */
  private pulse(node: Container, channel: "amount" | "title", scope: string, peak: number, halfSeconds: number): void {
    const generation = ++this.generation[channel];
    const from = node.scale.x;
    void this.runner.tickInScope(
      scope,
      (elapsedSeconds) => {
        const half = Math.floor(elapsedSeconds / halfSeconds);
        const k = sineInOut(elapsedSeconds / halfSeconds - half);
        const [a, b] = half === 0 ? [from, peak] : half % 2 === 1 ? [peak, 1] : [1, peak];
        const scale = a + (b - a) * k;
        node.scale.set(scale, scale);
      },
      () => generation !== this.generation[channel] || node.destroyed,
    );
  }

  private scaleTween(node: Container, from: number, to: number, seconds: number, easing: "backOut" | "quadOut", current: () => boolean): Promise<void> {
    return this.runner.tweenProgress(
      seconds,
      (k) => {
        if (!current() || node.destroyed) return;
        const scale = from + (to - from) * k;
        node.scale.set(scale, scale);
      },
      undefined,
      { easing },
    );
  }

  /** Once per showing (any path to the first titled tier, a skip included): the chainsaws come in. */
  private ensureChainsawsRevealed(): void {
    if (this.chainsawsRevealed) return;
    this.chainsawsRevealed = true;
    const generation = ++this.generation.chainsaws;
    this.chainsaws.forEach((saw, i) => void this.revealChainsaw(saw, i, generation));
  }

  /**
   * One chainsaw: a scale-pop and fade from below its spot with a down → up swing, then a rock and a bob around its
   * authored pose. A right-hand saw swings the other way (mirrored). Cocos angles are counter-clockwise, so the swing's
   * Cocos degrees turn sign here.
   */
  private async revealChainsaw(saw: BigWinView["chainsaws"][number], index: number, generation: number): Promise<void> {
    const m = BIG_WIN_MOTION.chainsaw;
    const { node, base } = saw;
    const dir = base.x < 0 ? 1 : -1;
    const current = (): boolean => generation === this.generation.chainsaws && !node.destroyed;
    node.visible = true;
    node.alpha = 0;
    node.scale.set(base.scaleX * m.fromScale, base.scaleY * m.fromScale);
    node.angle = base.angle - m.swingDownDegrees * dir;
    node.position.set(base.x, base.y + m.riseY);
    if (index > 0) await this.runner.sleep(m.staggerSeconds * index);
    if (!current()) return;
    void this.runner.tweenProgress(
      m.enterSeconds,
      (k) => {
        if (!current()) return;
        node.scale.set(base.scaleX * (m.fromScale + (1 - m.fromScale) * k), base.scaleY * (m.fromScale + (1 - m.fromScale) * k));
      },
      undefined,
      { easing: "backOut" },
    );
    void this.runner.tweenProgress(
      m.enterSeconds,
      (k) => {
        if (current()) node.alpha = k;
      },
      undefined,
      { easing: "linear" },
    );
    void this.runner.tweenProgress(
      m.swingSeconds + m.settleSeconds,
      (k) => {
        if (current()) node.position.y = base.y + m.riseY * (1 - k);
      },
      undefined,
      { easing: "backOut" },
    );
    const down = base.angle - m.swingDownDegrees * dir;
    const up = base.angle - m.swingUpDegrees * dir;
    await this.runner.tweenProgress(
      m.swingSeconds,
      (k) => {
        if (current()) node.angle = down + (up - down) * k;
      },
      undefined,
      { easing: "quadOut" },
    );
    if (!current()) return;
    await this.runner.tweenProgress(
      m.settleSeconds,
      (k) => {
        if (current()) node.angle = up + (base.angle - up) * k;
      },
      undefined,
      { easing: "quadInOut" },
    );
    if (!current()) return;
    // The idle: a rock around the rest angle and a bob around the rest height, in phase (up and counter-clockwise for
    // the left saw first, mirrored for the right).
    void this.runner.tickInScope(
      SCOPE.chainsaw,
      (elapsedSeconds) => {
        const swing = alternate(elapsedSeconds, m.idleHalfSeconds);
        node.angle = base.angle - m.idleDegrees * dir * swing;
        node.position.y = base.y - m.idleBobY * swing;
      },
      () => !current(),
    );
  }

  /** The random bursts through the count-up, until the settle bump, the exit or a hide. */
  private startSplatterBursts(): void {
    if (this.splatters.length === 0) return;
    const generation = ++this.generation.splatterLoop;
    void this.runSplatterLoop(generation);
  }

  private async runSplatterLoop(generation: number): Promise<void> {
    const m = BIG_WIN_MOTION.splatter;
    while (generation === this.generation.splatterLoop) {
      await this.runner.sleep(m.minGapSeconds + Math.random() * (m.maxGapSeconds - m.minGapSeconds), SCOPE.splatter);
      if (generation !== this.generation.splatterLoop) return;
      this.burstSplatters(1);
    }
  }

  private burstSplatters(count: number): void {
    for (let i = 0; i < count; i++) this.burstRandomSplatter();
  }

  /** One hidden, rested splatter pops on a touch large, drops to its size, and fades out slowly; then it rests. */
  private burstRandomSplatter(): void {
    const m = BIG_WIN_MOTION.splatter;
    const now = this.runner.engineSeconds;
    const pool = this.splatters.filter((s) => !s.node.visible && s.readyAtSeconds <= now);
    const splatter = pool[Math.floor(Math.random() * pool.length)];
    if (!splatter) return;
    const { node } = splatter;
    const showing = this.showing;
    node.visible = true;
    node.alpha = 1;
    node.scale.set(m.fromScale, m.fromScale);
    void this.runner.tweenProgress(
      m.scaleSeconds,
      (k) => {
        if (node.visible && !node.destroyed) node.scale.set(m.fromScale + (m.toScale - m.fromScale) * k, m.fromScale + (m.toScale - m.fromScale) * k);
      },
      undefined,
      { easing: "quadOut" },
    );
    void this.runner
      .tweenProgress(
        m.lifeSeconds,
        (k) => {
          if (node.visible && !node.destroyed) node.alpha = 1 - k;
        },
        undefined,
        { easing: "quadIn" },
      )
      .then(() => {
        if (node.destroyed || showing !== this.showing || !node.visible) return;
        node.visible = false;
        splatter.readyAtSeconds = this.runner.engineSeconds + m.restSeconds;
      });
  }
}
